// Resolves a CSV point-mode tract into an area shape: the real ReportAll parcel, an artificial polygon around it,
// or a hand-drawn outline. Every resolution keeps the original point so the reviewer can change their mind.
import { parcelCache, searchCoordinatesWithParcelService } from '@/utils/map/Parcel.js'
import { roundGeometryCoordinates, bufferPolygonAroundPoint, positionIsValid } from '@/utils/eudr/EudrGeometry.js'
import { isSyntheticParcelId } from '@/utils/eudr/EudrFeatureName.js'
import cloneDeep from 'lodash/cloneDeep'

// What the EU system imputes to a bare point, so the drawn area declares no more ground than the point would.
export const BUFFER_AREA_HA = 4

const AREA_TYPES = ['Polygon', 'MultiPolygon']

// Returns { parcelId, geometry } on a hit, null when no parcel exists there; network failures throw.
export async function lookupParcelAtPoint (longitude, latitude) {
  const cached = parcelCache.getParcelForCoordinate({ latitude, longitude })
  let parcels = cached?.parcels
  if (!parcels) {
    const result = await searchCoordinatesWithParcelService({ latitude, longitude })
    if (result?.parcels) parcelCache.addParcel(result.parcels)
    parcels = result?.parcels ?? null
  }
  const parcelId = parcels?.features?.[0]?.properties?.fields?.robust_id
  const geometry = parcels?.features?.[0]?.geometry
  return parcelId && geometry ? { parcelId, geometry } : null
}

// Everything revertFeatureToPoint needs to undo the resolution; taken once, before the point becomes a shape.
function snapshotPoint (feature) {
  if (feature.geometry?.type !== 'Point' || feature.properties?.sourcePoint) return
  const { parcelId, geometrySource, uploadKind, area } = feature.properties ?? {}
  feature.properties = {
    ...feature.properties,
    sourcePoint: { coordinates: [...feature.geometry.coordinates], parcelId, geometrySource, uploadKind, area }
  }
}

// Mutates the feature: the synthetic point:<lon>,<lat> parcelId becomes the real robust_id, so it groups and dedups.
export function applyParcelToPointFeature (feature, { parcelId, geometry }) {
  snapshotPoint(feature)
  feature.geometry = roundGeometryCoordinates(cloneDeep(geometry))
  feature.properties = {
    ...feature.properties,
    parcelId,
    geometrySource: 'parcels',
    uploadKind: 'csvParcel'
  }
}

// Mutates the feature, keeping the synthetic parcelId (no real parcel number to adopt). False when coords are unusable.
export function applyBufferToPointFeature (feature) {
  const [longitude, latitude] = feature.geometry?.coordinates ?? []
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return false
  snapshotPoint(feature)
  feature.geometry = bufferPolygonAroundPoint(longitude, latitude, BUFFER_AREA_HA)
  feature.properties = {
    ...feature.properties,
    area: BUFFER_AREA_HA,
    geometrySource: 'buffer',
    uploadKind: 'csvPointBuffer'
  }
  return true
}

// A hand-drawn outline replaces the point; like the buffer, it keeps the synthetic parcelId.
export function applyDrawnShapeToPointFeature (feature, geometry) {
  if (!AREA_TYPES.includes(geometry?.type)) return false
  snapshotPoint(feature)
  feature.geometry = roundGeometryCoordinates(cloneDeep(geometry))
  feature.properties = {
    ...feature.properties,
    geometrySource: 'drawn',
    uploadKind: 'csvPointDrawn'
  }
  return true
}

export function canRevertToPoint (feature) {
  const [lon, lat] = feature?.properties?.sourcePoint?.coordinates ?? []
  return AREA_TYPES.includes(feature?.geometry?.type) && positionIsValid(lon, lat)
}

// Undoes a fetch/draw: the shape becomes the original point again, whatever vertex edits happened in between.
export function revertFeatureToPoint (feature) {
  if (!canRevertToPoint(feature)) return false
  const { coordinates, parcelId, geometrySource, uploadKind, area } = feature.properties.sourcePoint
  feature.geometry = { type: 'Point', coordinates: [coordinates[0], coordinates[1]] }
  const props = { ...feature.properties, parcelId, geometrySource, uploadKind, area }
  delete props.sourcePoint
  if (area === undefined) delete props.area
  feature.properties = props
  return true
}

// Rounds to 6 dp (~0.1 m); the synthetic parcelId encodes the location and keys the composite merge, so it moves too.
// False when the rounded position is where the point already is.
export function movePointFeature (feature, lon, lat) {
  if (feature?.geometry?.type !== 'Point') return false
  const longitude = Number(lon.toFixed(6))
  const latitude = Number(lat.toFixed(6))
  const [currentLon, currentLat] = feature.geometry.coordinates ?? []
  if (currentLon === longitude && currentLat === latitude) return false
  feature.geometry = { type: 'Point', coordinates: [longitude, latitude] }
  const props = feature.properties
  if (props && isSyntheticParcelId(props.parcelId)) props.parcelId = `point:${longitude},${latitude}`
  return true
}
